import type { ComponentType, ReactNode } from 'react';
import { SvgIcon, type SvgIconProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { NavSectionId } from '../navSections';
import type { MenuEntry } from '../menuEntries';

// The outline icon set of the approved phone mockups (2 px round strokes on a
// 24 px grid; the path data is copied from them). Phone and tablet chrome only:
// the desktop header and its user menu keep the filled Material icons that
// navSections.ts and menuEntries.ts name, so they look exactly as before.
const Outline = styled(SvgIcon)({
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
});

function outline(name: string, shapes: ReactNode) {
    const Icon = (props: SvgIconProps) => (
        <Outline data-testid={`outline-${name}`} {...props}>
            {shapes}
        </Outline>
    );
    Icon.displayName = `Outline(${name})`;
    return Icon;
}

export const MarketsIcon = outline('markets', <path d="M3 17l5-5 4 4 9-9" />);
export const MonitorIcon = outline(
    'monitor',
    <path d="M2 12h4l3-7 4 14 3-7h6" />
);
export const PortfolioIcon = outline(
    'portfolio',
    <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M8 17v-5M12 17V7M16 17v-3" />
    </>
);
export const MenuIcon = outline('menu', <path d="M4 6h16M4 12h16M4 18h16" />);
export const CloseIcon = outline('close', <path d="M6 6l12 12M18 6L6 18" />);
export const BellIcon = outline(
    'bell',
    <>
        <path d="M6 16v-5a6 6 0 0 1 12 0v5l2 2H4z" />
        <path d="M10 21a2 2 0 0 0 4 0" />
    </>
);
export const SearchIcon = outline(
    'search',
    <>
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
    </>
);
export const AdvancedSearchIcon = outline(
    'advanced-search',
    <>
        <path d="M3 6h7M3 12h5M3 18h7" />
        <circle cx="15" cy="11" r="4" />
        <path d="M21 17l-3.2-3.2" />
    </>
);
export const SettingsIcon = outline(
    'settings',
    <>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9L7 7M17 17l2.1 2.1M4.9 19.1L7 17M17 7l2.1-2.1" />
    </>
);
export const ChatsIcon = outline('chats', <path d="M4 5h16v11H9l-5 4z" />);
export const GatesIcon = outline(
    'gates',
    <>
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
);
export const AdminIcon = outline(
    'admin',
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
);
export const MoonIcon = outline(
    'moon',
    <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />
);
export const SunIcon = outline(
    'sun',
    <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
);
export const LogoutIcon = outline(
    'logout',
    <path d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h11" />
);
export const ChevronDownIcon = outline(
    'chevron-down',
    <path d="M6 9l6 6 6-6" />
);
// The admin entries (the mockup shows only the Admin row itself; these follow
// its style).
export const UsersIcon = outline(
    'users',
    <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 5.5a3 3 0 0 1 0 5.5M18 14.5c1.8.8 3 2.6 3 5.5" />
    </>
);
export const SubscribersIcon = outline(
    'subscribers',
    <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M10 9l5 3-5 3z" />
    </>
);
export const AuditIcon = outline(
    'audit',
    <>
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
        <path d="M3 3v5h5M12 7v5l3 2" />
    </>
);

/** The account button of the condensed header: a grey disc with a white person. */
export function AvatarDisc() {
    return (
        <span
            data-testid="AvatarDisc"
            style={{
                width: 30,
                height: 30,
                flexShrink: 0,
                borderRadius: 15,
                background: '#bdbdbd',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
            >
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7z" />
            </svg>
        </span>
    );
}

export const TAB_ICONS: Record<NavSectionId, ComponentType<SvgIconProps>> = {
    markets: MarketsIcon,
    monitor: MonitorIcon,
    portfolio: PortfolioIcon,
};

const ENTRY_ICONS: Record<string, ComponentType<SvgIconProps>> = {
    settings: SettingsIcon,
    chats: ChatsIcon,
    gates: GatesIcon,
    'admin-users': UsersIcon,
    'admin-subscribers': SubscribersIcon,
    'admin-gates': GatesIcon,
    'admin-audit': AuditIcon,
};

/** The outline icon of a More-sheet entry; an entry without one keeps its own. */
export const entryIcon = (
    entry: Pick<MenuEntry, 'id' | 'icon'>
): ComponentType<SvgIconProps> => ENTRY_ICONS[entry.id] ?? entry.icon;
